import { ScrollToTop } from "@/shared/lib/ScrollToTop";
import { BaseButton } from "@/shared/ui/BaseButton";
import { BaseBreadCrumb } from "@/shared/ui/base-bread-crumb";
import { BaseDivider } from "@/shared/ui/base-divider";
import { OfferCargoScheduleInfo } from "@/widgets/offer-cargo-schedule-info";
import { Box, Container, Stack, Typography } from "@mui/material";
import { Link } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import type { AdminCargoCreatePageProps } from "../../model";
import { CargoInfo } from "./cargo-info";
import { CompanyInfo } from "./company-info";
import { DeliveryProposalInfo } from "./delivery-proposal-info";
import { DestinationsInfo } from "./destinations-info";

export const CargoCreatePreview = ({
  companies,
  previewValues,
  onBackToInProgressClick,
  onSubmitClick,
}: AdminCargoCreatePageProps) => {
  const links = [
    { href: "/admin/cargos", label: "荷物一覧" },
    { href: "/admin/cargos/create", label: "荷物の新規登録" },
  ];

  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleSubmitClick = useCallback(async () => {
    if (!previewValues) {
      return;
    }

    setIsSubmitting(true);
    await onSubmitClick(previewValues);
    setIsSubmitting(false);
  }, [onSubmitClick, previewValues]);

  if (!previewValues) {
    return (
      <Stack justifyContent="center" alignItems="center">
        <Typography variant="h6" color="error">
          荷物情報がありません
        </Typography>
      </Stack>
    );
  }

  return (
    <Box px={{ xs: 2, sm: 4 }}>
      <ScrollToTop />
      <BaseBreadCrumb links={links} sx={{ mb: 3 }} />

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
          }}
        >
          荷物の新規登録
        </Typography>
      </Box>

      <Box
        display="flex"
        mt={4}
        mb={2}
        bgcolor="grey.50"
        minHeight="600px"
        flexDirection={"column"}
        flexGrow={1}
        sx={{
          mx: { xs: -2, sm: 0 },
        }}
      >
        <Container
          maxWidth="md"
          sx={{
            bgcolor: "white",
            mt: 4,
            mb: 7,
            border: "1px solid",
            borderColor: "grey.200",
            borderRadius: "16px",
          }}
        >
          <Stack spacing={4} mt={3} mb={10} px={{ xs: 1, sm: 3 }}>
            <CompanyInfo
              previewValues={previewValues}
              companies={companies.status === "success" ? companies.data : null}
            />
            <BaseDivider />
            <OfferCargoScheduleInfo draftOfferCargo={previewValues} />
            <BaseDivider />
            <DestinationsInfo draftOfferCargo={previewValues} />
            <BaseDivider />
            <CargoInfo previewValues={previewValues} />
            <BaseDivider />
            <DeliveryProposalInfo deadlineAt={previewValues.deadlineAt} />
          </Stack>
        </Container>
      </Box>

      <Stack spacing={1} mb={2}>
        <Box display="flex" justifyContent="center">
          <Typography fontSize="12px">
            ※ 登録すると、配送提案の受付も開始します
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} justifyContent="center">
          <BaseButton
            LinkComponent={Link}
            variant="outlined"
            color="primary"
            sx={{ width: "96px", fontSize: "14px", fontWeight: 700 }}
            onClick={onBackToInProgressClick}
          >
            修正する
          </BaseButton>
          <BaseButton
            variant="contained"
            color="primary"
            sx={{ width: "240px", fontSize: "14px", fontWeight: 700 }}
            onClick={handleSubmitClick}
            disabled={isSubmitting}
          >
            登録する
          </BaseButton>
        </Stack>
      </Stack>
    </Box>
  );
};
